import React from 'react';
import { Card, CardHeader, CardContent, CardTitle } from './ui/Card';
import { Badge } from './ui/Badge';
import { ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Brain, AlertCircle, CheckCircle2 } from 'lucide-react';
import { SCREENING_SECTIONS, THERAPIST_EVALUATION_SECTIONS } from '../lib/screeningQuestions';

export default function AssessmentAnalysis({ assessmentData, isTherapistView = false }) {
    if (!assessmentData) {
        return <div className="p-12 text-center text-slate-500">Analysis data not available.</div>;
    }

    const { type, score, date, questions = [], answers = {}, remarks = {}, patientName } = assessmentData;
    const isScreening = type === 'Child Developmental Screening' || type === 'Child Developmental Screening (Final)';
    const isTherapistEval = type === 'Therapist Clinical Evaluation';
    
    let activeSections = null;
    if (isScreening) activeSections = SCREENING_SECTIONS;
    if (isTherapistEval) activeSections = THERAPIST_EVALUATION_SECTIONS;
    
    const useDynamicSections = isScreening || isTherapistEval;

    const maxScore = useDynamicSections 
        ? activeSections.flatMap(s => s.questions).filter(q => q.options).length 
        : (questions.length > 0 ? questions.length * 4 : 20);
    
    let finalScore = score;
    if (useDynamicSections) {
        const allQuestions = activeSections.flatMap(s => s.questions);
        // Broadly define success criteria for demo
        finalScore = allQuestions.filter(q => {
            const val = answers[q.id];
            return val === 'Yes' || val === 'Age Appropriate' || val === 'Interactive' || val === 'Good' || val === 'Excellent' || val === 'None';
        }).length;
    } else if (!finalScore) {
        finalScore = 14; // Fallback
    }

    const percentage = Math.max(0, Math.min(100, Math.round((finalScore / (maxScore || 1)) * 100)));

    const pieData = [
        { name: 'Achieved', value: percentage },
        { name: 'Needs Support', value: Math.max(0, 100 - percentage) },
    ];
    const COLORS = ['#0d9488', '#f1f5f9'];

    // Define scoring labels for reverse lookup
    const SCORES = { 
        4: "Always", 3: "Often", 2: "Sometimes", 1: "Rarely",
        'Yes': 'Yes', 'No': 'No', 'Not Sure': 'Not Sure',
        'With Help': 'With Help', 'Partially': 'Partially'
    };

    return (
        <div className="space-y-6">
            <div className="grid md:grid-cols-3 gap-6">
                <Card className="md:col-span-1 shadow-sm border-0 bg-gradient-to-b from-brand-50 to-white text-center rounded-[2rem]">
                    <CardContent className="pt-8 px-6 pb-8">
                        <div className="h-40 w-40 mx-auto relative mb-4">
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie data={pieData} cx="50%" cy="50%" innerRadius={60} outerRadius={80} fill="#8884d8" dataKey="value" stroke="none">
                                        {pieData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Pie>
                                </PieChart>
                            </ResponsiveContainer>
                            <div className="absolute inset-0 flex flex-col items-center justify-center">
                                <span className="text-3xl font-black text-brand-900">{percentage}%</span>
                            </div>
                        </div>
                        <h2 className="text-xl font-bold text-slate-900 mb-2">{isScreening ? 'Success Rate' : isTherapistEval ? 'Clinical Baseline' : 'Independence'}</h2>
                        <p className="text-[10px] uppercase font-black tracking-widest text-slate-400 mb-6 px-4">
                            {useDynamicSections ? 'Based on milestone and evaluation markers.' : 'Calculated from quantitative points.'}
                        </p>
                        <Badge variant={percentage > 70 ? "success" : "warning"} className="px-4 py-1.5 text-[10px] font-black uppercase tracking-widest rounded-xl">
                            {percentage > 70 ? 'On Track' : 'Needs Review'}
                        </Badge>
                    </CardContent>
                </Card>

                <div className="md:col-span-2 space-y-6">
                    <Card className="shadow-soft border-slate-50 rounded-[2rem] overflow-hidden">
                        <CardHeader className="p-8 pb-3 border-b border-slate-100 flex flex-row items-center justify-between">
                            <CardTitle className="text-xl font-black text-slate-900 font-outfit uppercase tracking-tight">Response Breakdown</CardTitle>
                            {useDynamicSections && <Badge className="bg-brand-50 text-brand-600 border-0 rounded-lg text-[10px] font-black uppercase tracking-widest px-3">{isTherapistEval ? 'Clinical Evaluation' : 'Screening Protocol'}</Badge>}
                        </CardHeader>
                        <CardContent className="pt-0 p-0 divide-y divide-slate-100 max-h-[400px] overflow-y-auto custom-scrollbar">
                            {useDynamicSections ? (
                                activeSections.map((section) => (
                                    <div key={section.id} className="p-6 space-y-4">
                                        <h4 className="text-[10px] font-black text-brand-600 uppercase tracking-widest px-2">{section.title}</h4>
                                        <div className="space-y-2">
                                            {section.questions.map((q) => {
                                                const answer = answers[q.id];
                                                const isCritical = answer === 'No' || answer === 'Not Sure' || answer?.includes('Delay') || answer?.includes('Level 1') || answer?.includes('Level 2') || answer?.includes('Level 3') || answer === 'Poor' || answer === 'Withdrawn' || (q.type === 'textarea' && answer);
                                                const isText = q.type === 'textarea' || q.type === 'text';
                                                
                                                if (isText && !answer) return null;

                                                return (
                                                    <div key={q.id} className={`p-5 rounded-[1.5rem] border transition-all ${isCritical && !isText ? 'bg-red-50/50 border-red-100' : 'bg-white border-slate-50 shadow-sm'}`}>
                                                        <div className="flex items-center justify-between gap-4">
                                                            <p className={`text-sm font-bold ${isCritical && !isText ? 'text-red-900' : 'text-slate-700'}`}>{q.label}</p>
                                                            {!isText && (
                                                                <Badge variant={isCritical ? 'destructive' : 'success'} className="rounded-lg text-[9px] uppercase font-black shrink-0 px-3 py-1">
                                                                    {answer || 'N/A'}
                                                                </Badge>
                                                            )}
                                                        </div>
                                                        {isText && answer && (
                                                            <p className="text-xs text-slate-500 italic mt-3 pl-4 border-l-4 border-brand-200 py-1 bg-brand-50/30 rounded-r-xl">"{answer}"</p>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))
                            ) : (
                                questions.length > 0 ? (
                                    questions.map((q, idx) => (
                                        <div key={idx} className="p-6 hover:bg-slate-50 transition-colors">
                                            <div className="flex gap-4">
                                                <div className="mt-1">
                                                    {(answers[idx] >= 3 || answers[idx] === 'Yes') ? <CheckCircle2 className="h-6 w-6 text-green-500" /> : <AlertCircle className="h-6 w-6 text-amber-500" />}
                                                </div>
                                                <div className="flex-1">
                                                    <p className="font-bold text-slate-900 text-base mb-1">{q}</p>
                                                    <Badge variant="outline" className="mb-3 rounded-lg text-[10px] font-black uppercase tracking-widest">{SCORES[answers[idx]] || 'N/A'}</Badge>
                                                    {remarks[idx] && (
                                                        <p className="text-xs text-slate-600 bg-white border border-slate-100 p-4 rounded-2xl italic shadow-sm">
                                                            "{remarks[idx]}"
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="p-12 text-sm text-slate-500 text-center font-bold uppercase tracking-widest">No detailed question data available.</div>
                                )
                            )}
                        </CardContent>
                    </Card>

                    {isTherapistView && (
                        <div className="p-6 bg-slate-900 rounded-[2rem] border border-slate-800 flex items-start gap-4 shadow-premium">
                            <Brain className="h-6 w-6 text-brand-400 shrink-0 mt-1" />
                            <div>
                                <h4 className="font-black text-white font-outfit uppercase tracking-tight text-lg mb-1">Clinical Insight</h4>
                                <p className="text-sm text-slate-400 leading-relaxed font-light">
                                    {isTherapistEval
                                        ? "This is a direct clinical evaluation performed by a specialist. These insights should heavily map directly to the active treatment and functional intervention plan."
                                        : isScreening 
                                        ? "This screening data highlights specific developmental benchmarks as reported by the caregiver. Use this to prioritize clinical focus during the next session." 
                                        : "Longitudinal analysis suggests these quantitative shifts are indicative of environmental adaptations."}
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
